import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";

import { Classification } from "../../../../api";
import { usePermissions } from "../../../../hooks";
import { ACTIONS } from "../../../../utils/constants";

import styles from "./Classification.module.scss";

const LIMIT_OPTIONS = [10, 20, 50];

function formatDate(date) {
  if (!date) {
    return "—";
  }

  const parsedDate = new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("es-GT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(parsedDate);
}

function getVisiblePages(currentPage, totalPages) {
  const pages = [];
  const startPage = Math.max(1, currentPage - 2);
  const endPage = Math.min(totalPages, currentPage + 2);

  for (let page = startPage; page <= endPage; page += 1) {
    pages.push(page);
  }

  return pages;
}

function getPaginationResponse(result, page, limit) {
  const response =
    result?.classifications || result?.response || result || {};

  const docs = Array.isArray(response.docs)
    ? response.docs
    : Array.isArray(response)
      ? response
      : [];

  const totalDocs =
    Number(response.totalDocs) ||
    Number(response.total) ||
    Number(response.totalItems) ||
    docs.length;

  const totalPages =
    Number(response.totalPages) ||
    Number(response.pages) ||
    Math.max(1, Math.ceil(totalDocs / limit));

  return {
    docs,
    page: Number(response.page) || page,
    limit: Number(response.limit) || limit,
    totalPages,
    totalDocs,
  };
}

function getSchemaName(classification) {
  const schema = classification?.schema;

  if (!schema) {
    return "Sin esquema";
  }

  if (typeof schema === "string") {
    return schema;
  }

  const code = schema.id || "";
  const name = schema.name || "";

  if (code && name) {
    return `${code} - ${name}`;
  }

  return name || code || "Sin esquema";
}

export function ClassificationPage() {
  const { hasAction } = usePermissions();
  const canAdd = hasAction("catalog_classification", ACTIONS.ADD);
  const canEdit = hasAction("catalog_classification", ACTIONS.MOD);
  const canDelete = hasAction("catalog_classification", ACTIONS.DEL);

  const location = useLocation();
  const actionMenuRef = useRef(null);
  const actionMenuPanelRef = useRef(null);

  const [classifications, setClassifications] = useState([]);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState(null);
  const [classificationToDelete, setClassificationToDelete] =
    useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalDocs: 0,
  });

  const [searchValue, setSearchValue] = useState("");
  const [successMessage, setSuccessMessage] = useState(
    location.state?.success || ""
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadClassifications = useCallback(
    async (
      page = 1,
      limit = pagination.limit,
      search = searchValue
    ) => {
      try {
        setIsLoading(true);
        setError("");
        setOpenActionMenu(null);

        const classificationCtrl = new Classification();

        const filters = {};

        if (search.trim()) {
          filters.search = search.trim();
        }

        const result = await classificationCtrl.getClassifications(
          page,
          limit,
          filters
        );

        const response = getPaginationResponse(result, page, limit);

        setClassifications(response.docs);

        setPagination({
          page: response.page,
          limit: response.limit,
          totalPages: response.totalPages,
          totalDocs: response.totalDocs,
        });
      } catch (errorResponse) {
        console.error("loadClassifications error:", errorResponse);

        setClassifications([]);

        setPagination({
          page: 1,
          limit: pagination.limit,
          totalPages: 1,
          totalDocs: 0,
        });

        setError(
          errorResponse?.msg ||
            "No fue posible cargar las clasificaciones. Intenta nuevamente."
        );
      } finally {
        setIsLoading(false);
      }
    },
    [pagination.limit, searchValue]
  );

  useEffect(() => {
    loadClassifications();
  }, []);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = window.setTimeout(() => {
      setSuccessMessage("");
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [successMessage]);

  useEffect(() => {
    function handlePointerDown(event) {
      const clickedTrigger = actionMenuRef.current?.contains(event.target);
      const clickedPanel = actionMenuPanelRef.current?.contains(event.target);

      if (!clickedTrigger && !clickedPanel) {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpenActionMenu(null);
        setActionMenuPosition(null);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useEffect(() => {
    if (openActionMenu === null) {
      return undefined;
    }

    function handleReposition() {
      setOpenActionMenu(null);
      setActionMenuPosition(null);
    }

    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [openActionMenu]);

  function handleSearchSubmit(event) {
    event.preventDefault();

    loadClassifications(1, pagination.limit, searchValue);
  }

  function handleClearSearch() {
    setSearchValue("");
    loadClassifications(1, pagination.limit, "");
  }

  function handlePageChange(page) {
    if (
      page < 1 ||
      page > pagination.totalPages ||
      page === pagination.page
    ) {
      return;
    }

    loadClassifications(page);
  }

  function handleLimitChange(event) {
    const nextLimit = Number(event.target.value);

    loadClassifications(1, nextLimit);
  }

  function handleOpenDeleteModal(classification) {
    setOpenActionMenu(null);
    setActionMenuPosition(null);
    setClassificationToDelete(classification);
    setError("");
  }

  function handleCloseDeleteModal() {
    if (isDeleting) {
      return;
    }

    setClassificationToDelete(null);
  }

  async function handleConfirmDelete() {
    if (!classificationToDelete) {
      return;
    }

    try {
      setIsDeleting(true);
      setError("");

      const classificationCtrl = new Classification();

      await classificationCtrl.deleteClassification(
        classificationToDelete._id
      );

      setSuccessMessage("Clasificación eliminada correctamente.");
      setClassificationToDelete(null);

      const nextPage =
        classifications.length === 1 && pagination.page > 1
          ? pagination.page - 1
          : pagination.page;

      await loadClassifications(nextPage);
    } catch (errorResponse) {
      console.error("handleConfirmDelete error:", errorResponse);

      setError(
        errorResponse?.msg ||
          "No fue posible eliminar la clasificación. Intenta nuevamente."
      );
    } finally {
      setIsDeleting(false);
    }
  }

  const visiblePages = getVisiblePages(
    pagination.page,
    pagination.totalPages
  );

  const firstClassificationNumber =
    pagination.totalDocs === 0
      ? 0
      : (pagination.page - 1) * pagination.limit + 1;

  const lastClassificationNumber = Math.min(
    pagination.page * pagination.limit,
    pagination.totalDocs
  );

  return (
    <section className={styles.ClassificationPage}>
      <div className={styles.TopBar}>
        <form
          className={styles.SearchForm}
          onSubmit={handleSearchSubmit}
        >
          <input
            type="search"
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
            placeholder="Buscar por nombre, código o descripción..."
            aria-label="Buscar clasificaciones"
          />

          <button type="submit" aria-label="Buscar clasificaciones">
            ⌕
          </button>
        </form>

        <div className={styles.TopActions}>
          {canAdd && (
            <Link
              to="/admin/catalog/classification/create"
              className={styles.CreateButton}
            >
              <span>＋</span>
              Nueva clasificación
            </Link>
          )}
        </div>
      </div>

      {searchValue.trim() && (
        <div className={styles.Filters}>
          <span className={styles.ActiveSearch}>
            Búsqueda: <strong>{searchValue.trim()}</strong>
          </span>

          <button
            type="button"
            className={styles.ClearButton}
            onClick={handleClearSearch}
          >
            Limpiar búsqueda
          </button>
        </div>
      )}

      {successMessage && (
        <div className={styles.SuccessMessage} role="status">
          {successMessage}
        </div>
      )}

      {error && (
        <div className={styles.ErrorMessage} role="alert">
          {error}
        </div>
      )}

      {isLoading ? (
        <div className={styles.StateMessage}>
          Cargando clasificaciones...
        </div>
      ) : classifications.length === 0 ? (
        <div className={styles.EmptyState}>
          <p>No hay clasificaciones para mostrar.</p>
        </div>
      ) : (
        <>
          <p className={styles.TableInfo}>
            Mostrando {firstClassificationNumber} a{" "}
            {lastClassificationNumber} de {pagination.totalDocs}{" "}
            clasificaciones
          </p>

          <div className={styles.TableContainer}>
            <table className={styles.Table}>
              <thead>
                <tr>
                  <th>Esquema</th>
                  <th>Código</th>
                  <th>Nombre</th>
                  <th>Descripción</th>
                  <th>Orden</th>
                  <th>Fecha creación</th>
                  <th className={styles.ActionsColumn}>Acciones</th>
                </tr>
              </thead>

              <tbody>
                {classifications.map((classification) => (
                  <tr key={classification._id}>
                    <td
                      className={styles.SchemaCell}
                      title={getSchemaName(classification)}
                    >
                      {getSchemaName(classification)}
                    </td>

                    <td>
                      <span
                        className={styles.ClassificationCode}
                        title={classification.id}
                      >
                        {classification.id}
                      </span>
                    </td>

                    <td>
                      <p
                        className={styles.ClassificationName}
                        title={classification.name}
                      >
                        {classification.name}
                      </p>
                    </td>

                    <td>
                      <p
                        className={styles.Description}
                        title={classification.description || "Sin descripción"}
                      >
                        {classification.description || "Sin descripción"}
                      </p>
                    </td>

                    <td>
                      <span className={styles.OrderBadge}>
                        {classification.order}
                      </span>
                    </td>

                    <td className={styles.DateCell}>
                      {formatDate(classification.createdate)}
                    </td>

                    <td className={styles.ActionsColumn}>
                      {(canEdit || canDelete) && (
                      <div
                        ref={
                          openActionMenu === classification._id
                            ? actionMenuRef
                            : null
                        }
                        className={styles.RowActions}
                      >
                        <button
                          type="button"
                          className={styles.MenuButton}
                          aria-label={`Acciones para ${classification.name}`}
                          aria-expanded={
                            openActionMenu === classification._id
                          }
                          aria-haspopup="menu"
                          onClick={(event) => {
                            if (openActionMenu === classification._id) {
                              setOpenActionMenu(null);
                              setActionMenuPosition(null);
                              return;
                            }

                            const rect =
                              event.currentTarget.getBoundingClientRect();
                            const menuWidth = 210;
                            const viewportRight =
                              document.documentElement.clientWidth;
                            const left = Math.min(
                              rect.right + window.scrollX - menuWidth,
                              viewportRight + window.scrollX - menuWidth - 8
                            );

                            setActionMenuPosition({
                              top: rect.bottom + window.scrollY + 6,
                              left: Math.max(left, 8),
                            });
                            setOpenActionMenu(classification._id);
                          }}
                        >
                          ⋮
                        </button>

                        {openActionMenu === classification._id &&
                          actionMenuPosition &&
                          createPortal(
                            <div
                              ref={actionMenuPanelRef}
                              className={styles.ActionMenu}
                              style={{
                                position: "absolute",
                                top: actionMenuPosition.top,
                                left: actionMenuPosition.left,
                              }}
                              role="menu"
                            >
                              {canEdit && (
                              <Link
                                to={`/admin/catalog/classification/${classification._id}/edit`}
                                role="menuitem"
                                onClick={() => {
                                  setOpenActionMenu(null);
                                  setActionMenuPosition(null);
                                }}
                              >
                                Editar clasificación
                              </Link>
                              )}

                              {canDelete && (
                              <button
                                type="button"
                                className={styles.DeleteAction}
                                role="menuitem"
                                onClick={() =>
                                  handleOpenDeleteModal(classification)
                                }
                              >
                                Eliminar clasificación
                              </button>
                              )}
                            </div>,
                            document.body
                          )}
                      </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <footer className={styles.Pagination}>
            <label className={styles.LimitControl}>
              <span>Mostrar:</span>

              <select
                value={pagination.limit}
                onChange={handleLimitChange}
              >
                {LIMIT_OPTIONS.map((limit) => (
                  <option key={limit} value={limit}>
                    {limit}
                  </option>
                ))}
              </select>
            </label>

            <div className={styles.PaginationButtons}>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={pagination.page <= 1}
                aria-label="Ir a la primera página"
              >
                «
              </button>

              <button
                type="button"
                onClick={() =>
                  handlePageChange(pagination.page - 1)
                }
                disabled={pagination.page <= 1}
                aria-label="Ir a la página anterior"
              >
                ‹
              </button>

              {visiblePages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => handlePageChange(page)}
                  className={
                    page === pagination.page
                      ? styles.CurrentPage
                      : ""
                  }
                  aria-label={`Ir a la página ${page}`}
                  aria-current={
                    page === pagination.page ? "page" : undefined
                  }
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                onClick={() =>
                  handlePageChange(pagination.page + 1)
                }
                disabled={pagination.page >= pagination.totalPages}
                aria-label="Ir a la página siguiente"
              >
                ›
              </button>

              <button
                type="button"
                onClick={() =>
                  handlePageChange(pagination.totalPages)
                }
                disabled={pagination.page >= pagination.totalPages}
                aria-label="Ir a la última página"
              >
                »
              </button>
            </div>
          </footer>
        </>
      )}

      {classificationToDelete && (
        <div className={styles.DeleteModalOverlay}>
          <div
            className={styles.DeleteModal}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="delete-classification-title"
            aria-describedby="delete-classification-description"
          >
            <div className={styles.DeleteModalIcon}>!</div>

            <h2 id="delete-classification-title">
              Eliminar clasificación
            </h2>

            <p id="delete-classification-description">
              ¿Deseas eliminar la clasificación{" "}
              <strong>{classificationToDelete.name}</strong>?
            </p>

            <p className={styles.DeleteModalWarning}>
              La clasificación dejará de mostrarse en el listado y no podrá
              utilizarse en nuevos registros.
            </p>

            <div className={styles.DeleteModalActions}>
              <button
                type="button"
                className={styles.CancelDeleteButton}
                onClick={handleCloseDeleteModal}
                disabled={isDeleting}
                autoFocus
              >
                Cancelar
              </button>

              <button
                type="button"
                className={styles.ConfirmDeleteButton}
                onClick={handleConfirmDelete}
                disabled={isDeleting}
              >
                {isDeleting
                  ? "Eliminando..."
                  : "Eliminar clasificación"}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}